import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, Gift, Sparkles, Star, Crown, ShoppingBag,
  Sprout, BadgeCheck, AlertTriangle, Award, GitBranch, ArchiveRestore,
} from "lucide-react";
import { api } from "../lib/apiClient";

/**
 * v3.99af — Dual-pulse ActivityTicker.
 *
 * Renders a continuously-scrolling marquee of real, privacy-safe events
 * scoped to either the **Registry Pulse** (proof / preservation / lineage)
 * or the **Market Pulse** (drops / auctions / giveaways / breeder onboards).
 * Pass `pulse="registry"` or `pulse="market"`. The legacy single-ticker
 * usage on /trending continues to work via `pulse="all"`.
 *
 * Visual direction: Bloomberg-terminal restraint. Slow scroll, mono labels,
 * subtle accent dot per category. No flashing, no loud color.
 *
 * Admin control: events are derived from underlying records. Flagging a
 * grow log, removing a listing, or rejecting a review automatically drops
 * those events from the ticker — no separate hide list needed.
 */

const ICON_FOR_KIND = {
  // Market
  giveaway_entry: Gift,
  giveaway_won: Crown,
  listing_drop: ShoppingBag,
  breeder_joined: Sparkles,
  // Registry
  review: Star,
  grow_log_created: Sprout,
  grow_breeder_verified: BadgeCheck,
  grow_milestone: Award,
  grow_herm_report: AlertTriangle,
  lineage_added: GitBranch,
  vault_preserved: ArchiveRestore,
};

const ACCENT_FOR_KIND = {
  // Market — gold heritage.
  giveaway_entry: "var(--gold)",
  giveaway_won: "var(--gold)",
  listing_drop: "var(--gold)",
  breeder_joined: "var(--gold)",
  // Registry — green for verified, blue for lineage/info, red for herm.
  review: "var(--primary-text)",
  grow_log_created: "var(--primary-text)",
  grow_breeder_verified: "var(--primary-text)",
  grow_milestone: "var(--primary-text)",
  grow_herm_report: "#D89393",
  lineage_added: "#7CC9FF",
  vault_preserved: "#D8B36C",
};

const PULSE_META = {
  registry: { label: "Registry", accent: "var(--primary-text)" },
  market:   { label: "Market",   accent: "var(--gold)" },
  all:      { label: "Live",     accent: "var(--secondary)" },
};

export default function ActivityTicker({
  limit = 24,
  intervalMs = 60000,
  pulse = "all",
  compact = false,
  className = "",
}) {
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      api.get("/public/activity-ticker", { params: { limit, pulse } })
        .then((r) => { if (alive) setItems(((r.data || {}).items) || []); })
        .catch(() => { /* soft-fail */ });
    };
    load();
    const t = setInterval(load, intervalMs);
    return () => { alive = false; clearInterval(t); };
  }, [limit, intervalMs, pulse]);

  if (!items.length) return null;

  // Animation duration scales with the number of items so density feels
  // consistent: ~5 seconds per item, clamped to a sensible range.
  const durationSec = Math.max(28, Math.min(120, items.length * 5));
  const meta = PULSE_META[pulse] || PULSE_META.all;

  // v3.99aw — compact mode used for the mobile unified pulse ribbon.
  // Tighter vertical metric, no top-level "Live" label slug, and every
  // event carries its REGISTRY / MARKET category prefix so users can
  // distinguish streams in the single ribbon.
  const padY = compact ? "py-1.5" : "py-2";
  const gapItem = compact ? "gap-6" : "gap-8 md:gap-12";

  return (
    <div
      className={`w-full border-y border-[var(--border)] ${className}`}
      style={{
        background: "linear-gradient(180deg, var(--surface), var(--surface-2))",
      }}
      data-testid={`activity-ticker-${pulse}${compact ? "-compact" : ""}`}
      role="region"
      aria-label={`${meta.label} pulse`}
    >
      <div className={`max-w-[1400px] mx-auto px-4 md:px-6 ${padY} flex items-center gap-3 md:gap-4 overflow-hidden`}>
        {!compact && (
          <>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] shrink-0"
                 style={{ color: meta.accent }}>
              <Radio className="w-3 h-3 animate-pulse" />
              <span className="hidden sm:inline">{meta.label}</span>
              <span className="sm:hidden">{meta.label.slice(0, 3)}</span>
            </div>
            <div className="h-4 w-px bg-[var(--border)] shrink-0 hidden sm:block" />
          </>
        )}
        {compact && (
          <Radio className="w-3 h-3 shrink-0 animate-pulse" style={{ color: meta.accent }} />
        )}
        <div
          className="relative flex-1 overflow-hidden ticker-mask"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            className={`ticker-track flex ${gapItem} w-max`}
            style={{
              animation: `ticker-scroll ${durationSec}s linear infinite`,
              animationPlayState: paused ? "paused" : "running",
            }}
          >
            {[0, 1].map((dup) => (
              <div key={dup} className={`flex items-center ${gapItem} shrink-0`} aria-hidden={dup === 1}>
                {items.map((ev, i) => {
                  const Icon = ICON_FOR_KIND[ev.kind] || Sparkles;
                  const accent = ACCENT_FOR_KIND[ev.kind] || "var(--text-muted)";
                  // In compact mode prepend each item with its category so
                  // the unified mobile ribbon stays legible without a
                  // separate top-level label.
                  const categoryPrefix = compact && ev.pulse
                    ? (ev.pulse === "registry" ? "REGISTRY" : ev.pulse === "market" ? "MARKET" : null)
                    : null;
                  const inner = (
                    <span className={`inline-flex items-center gap-2 whitespace-nowrap ${compact ? "text-[11px]" : "text-xs md:text-[13px]"}`}
                          style={{ color: "var(--text-2)" }}>
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
                      {categoryPrefix && (
                        <span className="font-mono uppercase tracking-[0.16em] text-[9px] shrink-0"
                              style={{ color: ev.pulse === "registry" ? "var(--primary-text)" : "var(--gold)" }}>
                          {categoryPrefix}
                        </span>
                      )}
                      <span>{ev.text}</span>
                    </span>
                  );
                  return ev.href ? (
                    <Link
                      key={`${dup}-${i}`}
                      to={ev.href}
                      className="hover:text-[var(--text)] transition-colors"
                      data-testid={dup === 0 ? `ticker-${pulse}-item-${i}` : undefined}
                    >
                      {inner}
                    </Link>
                  ) : (
                    <span key={`${dup}-${i}`} data-testid={dup === 0 ? `ticker-${pulse}-item-${i}` : undefined}>
                      {inner}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-mask {
          -webkit-mask-image: linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%);
                  mask-image: linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%);
        }
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation: none !important; transform: translateX(0) !important; }
        }
      `}</style>
    </div>
  );
}
